import React, { useEffect, useState } from "react";
import {
    IconWorldWww,
    IconPackage,
    IconTruckDelivery,
    IconInfoCircle,
    IconCircleCheckFilled,
    IconShieldLock,
    IconLoader2,
    IconRefresh
} from "@tabler/icons-react";
import { useFetchWebhookDetails, useWebhookRegisterMutation } from "../../../apis/settingsApis";
import toast from "react-hot-toast";

const Webhooks: React.FC = () => {
    // 1. Local State
    const [webhookUrl, setWebhookUrl] = useState("");
    const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
    const [webhookRegistered, setWebhookRegistered] = useState<boolean>(false);

    // 2. API Hooks
    const { mutate: registerWebhook, isPending: isSubmitting } = useWebhookRegisterMutation();
    const { data: webhookDetails, isPending: isFetching, refetch } = useFetchWebhookDetails();

    // 3. Derived State
    // "isActive" is true if we have data from server OR we just successfully registered in this session
    const isActive = !!webhookDetails?.webhookUrl || webhookRegistered;

    // Sync server data to local state on load
    useEffect(() => {
        if (webhookDetails) {
            setWebhookUrl(webhookDetails?.webhookUrl || "");
            setSelectedGroups(webhookDetails?.events || []);
        }
    }, [webhookDetails]);

    useEffect(() => {
        refetch();
    }, [])

    // Check if the current form differs from the last saved server data
    const hasChanges =
        webhookUrl !== (webhookDetails?.webhookUrl || "") ||
        JSON.stringify([...selectedGroups].sort()) !== JSON.stringify([...(webhookDetails?.events || [])].sort());

    const isFormInvalid = !webhookUrl.trim() || !webhookUrl.startsWith("https://") || selectedGroups.length === 0;

    // Button is disabled if: Invalid, Submitting, or (Already Active and nothing has changed)
    const isButtonDisabled = isFormInvalid || isSubmitting || (isActive && !hasChanges);

    const eventGroups = [
        {
            id: "ORDER_UPDATES",
            title: "Order Status Updates",
            description: "Covers the entire lifecycle from creation to final delivery.",
            icon: <IconPackage size={24} className="text-blue-500" />,
            statuses: [
                { code: "NEW_ORDER", desc: "Order created in system" },
                { code: "CANCELLED", desc: "Order voided by merchant" },
                { code: "PICKUP_GENERATED", desc: "Manifest created" },
                { code: "READY_FOR_PICKUP", desc: "Assigned to driver" },
                { code: "PICKUP_COLLECTED", desc: "Scanned by courier" },
                { code: "RECEIVE_IN_WAREHOUSE", desc: "Arrived at hub" },
                { code: "OUT_FOR_DELIVERY", desc: "With delivery agent" },
                { code: "DELIVERED", desc: "Handed to receiver" },
                { code: "NOT_DELIVERED", desc: "Delivery failed" },
                { code: "RETURNED_TO_SHIPPER", desc: "Returned to sender" }
            ]
        },
        {
            id: "PRODUCT_UPDATES",
            title: "Product Tracking Updates",
            description: "Catalog management and inventory quantity shifts.",
            icon: <IconTruckDelivery size={24} className="text-green-500" />,
            statuses: [
                { code: "CREATE", desc: "Product created in system" },
                { code: "UPDATE", desc: "Product updated in system" },
                { code: "DELETE", desc: "Product removed from system" },
                { code: "STATUS", desc: "Status toggled (Active/Inactive)" },
                { code: "QTY_UPDATE", desc: "Stock levels modified" },
            ]
        }
    ];

    const toggleGroup = (groupId: string) => {
        setSelectedGroups(prev =>
            prev.includes(groupId) ? prev.filter(g => g !== groupId) : [...prev, groupId]
        );
    };

    const handleSubmit = () => {
        const payload = {
            webhook_url: webhookUrl,
            subscriptions: selectedGroups,
            deployed_at: new Date().toISOString()
        };

        registerWebhook(payload, {
            onSuccess: () => {
                setWebhookRegistered(true); // Forces UI into "Active" mode immediately
                toast.success(isActive ? "Configuration Updated" : "Webhook Registered Successfully");
            },
            onError: (error: any) => {
                toast.error(error?.message || "Action failed");
            },
        });
    };

    if (isFetching) {
        return (
            <div className="w-full max-w-3xl h-64 flex flex-col items-center justify-center bg-white rounded-3xl border border-gray-100 shadow-sm">
                <IconLoader2 className="animate-spin text-gray-300 mb-4" size={40} />
                <p className="text-gray-400 font-medium text-sm">Loading configuration...</p>
            </div>
        );
    }

    return (
        <div className="w-full max-w-3xl bg-white rounded-3xl shadow-sm border border-gray-100 p-8">

            {/* Step 1: Destination */}
            <div className="mb-10">
                <div className="flex justify-between items-start mb-4">
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2 text-gray-400 font-bold text-xs uppercase tracking-widest">
                            <IconWorldWww size={18} /> Step 1: Destination URL
                        </div>
                    </div>
                    {isActive && (
                        <span className="px-3 py-1 bg-green-100 text-green-700 text-[10px] font-black uppercase tracking-wider rounded-full flex items-center gap-1">
                            <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                            Active Connection
                        </span>
                    )}
                </div>

                <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-2xl mb-4">
                    <IconShieldLock size={20} className="text-blue-600 shrink-0 mt-0.5" />
                    <p className="text-[11px] text-blue-700 leading-relaxed font-medium">
                        <strong>HTTPS Required:</strong> Update are sent via Get Method. Ensure your endpoint is publicly accessible and returns 200 OK.
                    </p>
                </div>

                <input
                    type="url"
                    value={webhookUrl}
                    onChange={(e) => setWebhookUrl(e.target.value)}
                    placeholder="https://your-api.com/webhooks"
                    className={`w-full p-4 border-2 rounded-2xl transition-all outline-none font-mono text-sm shadow-inner ${
                        webhookUrl && !webhookUrl.startsWith("https://")
                            ? "border-red-200 bg-red-50"
                            : "bg-gray-50 border-transparent focus:border-yellow-400 focus:bg-white"
                    }`}
                />
            </div>

            {/* Step 2: Selection */}
            <div className="mb-10">
                <div className="flex items-center gap-2 mb-4 text-gray-400 font-bold text-xs uppercase tracking-widest">
                    <IconInfoCircle size={18} /> Step 2: Subscription Groups
                </div>

                <div className="space-y-3">
                    {eventGroups.map((group) => (
                        <div
                            key={group.id}
                            onClick={() => toggleGroup(group.id)}
                            className={`group relative flex items-center justify-between p-5 rounded-2xl border-2 transition-all cursor-pointer ${
                                selectedGroups.includes(group.id)
                                    ? 'border-yellow-400 bg-yellow-50/50'
                                    : 'border-gray-50 bg-gray-50/50 hover:border-gray-200'
                            }`}
                        >
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-white rounded-xl shadow-sm">
                                    {group.icon}
                                </div>
                                <div>
                                    <h4 className="font-bold text-gray-800 text-sm">{group.title}</h4>
                                    <p className="text-[11px] text-gray-500">{group.description}</p>
                                </div>
                            </div>
                            
                            <div className="flex items-center gap-3">
                                {/* Hover Tooltip for Status Codes */}
                                <div className="opacity-0 group-hover:opacity-100 absolute bottom-full right-0 mb-3 w-72 p-4 bg-gray-900 text-white rounded-2xl shadow-2xl pointer-events-none transition-all z-50 transform translate-y-2 group-hover:translate-y-0">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-yellow-400 mb-2">Technical Statuses:</p>
                                    <div className="space-y-1.5">
                                        {group.statuses.map(s => (
                                            <div key={s.code} className="flex justify-between text-[11px] border-b border-gray-800 pb-1 last:border-0">
                                                <span className="font-mono font-bold text-blue-300">{s.code}</span>
                                                <span className="opacity-60 italic">{s.desc}</span>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="absolute bottom-[-6px] right-8 w-3 h-3 bg-gray-900 rotate-45"></div>
                                </div>

                                {selectedGroups.includes(group.id) ? (
                                    <IconCircleCheckFilled size={30} className="text-yellow-400" />
                                ) : (
                                    <div className="w-7 h-7 rounded-full border-2 border-gray-200 bg-white" />
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Action Button */}
            <button
                onClick={handleSubmit}
                disabled={isButtonDisabled}
                className={`w-full py-4 rounded-2xl font-black uppercase tracking-widest text-sm transition-all shadow-xl flex items-center justify-center gap-2 ${
                    isButtonDisabled
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed shadow-none"
                        : "bg-gray-900 text-white hover:bg-black active:scale-[0.98]"
                }`}
            >
                {isSubmitting ? (
                    <IconLoader2 className="animate-spin" size={20} />
                ) : isActive ? (
                    <IconRefresh size={18} />
                ) : null}

                {isSubmitting
                    ? "Processing..."
                    : isActive
                        ? (hasChanges ? "Save Changes" : "No Changes Detected")
                        : "Enable Webhook Updates"}
            </button>
        </div>
    );
};

export default Webhooks;